export const DEFAULT_TIME_ZONE = 'Europe/Madrid';
export const EMPTY_VALUE = '—';

const toNumber = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
};

const trimZeros = (value) => value.replace(/\.0+$/, '');

// $24.03M, $258K, $950 — compact money used across the banner.
// `trim` drops trailing zero decimals, e.g. $50.00M -> $50M.
export const formatMoney = (value, { trim = false } = {}) => {
	const number = toNumber(value);
	if (number === null) return EMPTY_VALUE;

	const sign = number < 0 ? '-' : '';
	const abs = Math.abs(number);
	let text;

	if (abs >= 1e9) {
		text = `${(abs / 1e9).toFixed(2)}B`;
	} else if (abs >= 1e6 || Math.round(abs / 1e3) >= 1000) {
		text = `${(abs / 1e6).toFixed(2)}M`;
	} else if (abs >= 1e3) {
		text = `${Math.round(abs / 1e3)}K`;
	} else {
		text = `${Math.round(abs)}`;
	}

	if (trim) text = text.replace(/\.0+([BM])$/, '$1');
	return `${sign}$${text}`;
};

export const formatPercent = (value, { decimals = 0, signed = false } = {}) => {
	const number = toNumber(value);
	if (number === null) return EMPTY_VALUE;
	const text = trimZeros(number.toFixed(decimals));
	return `${signed && number > 0 ? '+' : ''}${text}%`;
};

export const formatRatio = (value) => {
	const number = toNumber(value);
	if (number === null) return EMPTY_VALUE;
	return `${trimZeros(number.toFixed(1))}×`;
};

export const formatInteger = (value) => new Intl.NumberFormat('en-US').format(value);

export const padTwo = (value) => String(value).padStart(2, '0');

export const isNegative = (value) => {
	const number = toNumber(value);
	return number !== null && number < 0;
};

export const isPositive = (value) => {
	const number = toNumber(value);
	return number !== null && number > 0;
};

// "21 Sept, 18:29 CEST" rendered in the target time zone.
export const formatSnapshot = (value, timeZone = DEFAULT_TIME_ZONE) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';

	try {
		const parts = new Intl.DateTimeFormat('en-GB', {
			timeZone,
			day: 'numeric',
			month: 'short',
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23',
			timeZoneName: 'short'
		}).formatToParts(date);
		const part = (type) => parts.find((item) => item.type === type)?.value || '';
		return `${part('day')} ${part('month')}, ${part('hour')}:${part('minute')} ${part('timeZoneName')}`;
	} catch {
		return '';
	}
};

// 1 Jan next year, 00:00 Europe/Madrid (CET, always +01:00 in January).
// Used until the API provides the authoritative deadline.
export const getFallbackDeadline = () => new Date(`${new Date().getFullYear() + 1}-01-01T00:00:00+01:00`);

export const parseDeadline = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};
